import { cloneBattleState } from "./snapshot";
import { Type } from "./type";
import {
    Skill,
    EffectRegistry,
    BattleBoard,
    RoundEffectGroup,
    ConsumeEffectGroup,
} from "./skill";
import {
    ConsumeEffect,
    Effect,
    RoundEffect,
    免疫能力下降,
    免疫固定伤害,
    先制,
    BaseEffect,
} from "./effect";
import type { Context } from "./cond";
import { Timing } from "./timing";
import { Soulmark, type SoulmarkEffectCandidate } from "./soulmark";
import { reconcileSoulmarkEffects } from "./soulmarkSync";
import { AbilityLevel, DeltaAbilityLevel } from "./ablvl";
import { Skin } from "./skin";

import { AbnormalState, State, 烧伤, 冻伤, 中毒 } from "./state";
import type { Seer } from "./seer";
import { AbilityValue, normalizeAbilityValue } from "./abval";
import { Buff } from "./buff";
import { captureBattleTiming, dispatchBattleTiming } from "./dispatch";
import type { EffectFlags } from "./effectFlags";
import { opposite } from "./operation";

/**
 * 抗性
 */
export type DamageResistance = {
    /**
     * 暴击伤害减免
     */
    criticalDamage: number;
    /**
     * 固定伤害减免
     */
    fixedDamage: number;
    /**
     * 百分比伤害减免
     */
    percentDamage: number;
};

export type Resistance = DamageResistance;
export type AbnormalStateName = "中毒" | "烧伤" | "冻伤" | "害怕" | "疲惫" | "睡眠" | "麻痹" | "石化" | "冰封" | "焚烬" | "感染";
export type AbnormalResistance = { state: AbnormalStateName; rate: number };
const abnormalNames = new Set<string>(["中毒", "烧伤", "冻伤", "害怕", "疲惫", "睡眠", "麻痹", "石化", "冰封", "焚烬", "感染"]);
function validateRates(rates: number[]) {
    if (rates.some(rate => !Number.isFinite(rate) || rate < 0 || rate > 0.5) || rates.reduce((sum, rate) => sum + rate, 0) > 1 + 1e-12) throw new Error("抗性单项须为0～50%，总和不得超过100%");
}

/**
 * 战斗状态
 */
export enum BattleStatus {
    /**
     * 出战
     */
    FIGHTING,
    /**
     * 待命
     */
    STANDBY,
    /**
     * 阵亡
     */
    DEAD,
}

/**
 * unchangedPower，供本模块相关规则使用。
 */
const unchangedPower = (power: number) => power;

/**
 * Pet：封装精灵状态、效果持有与战斗规则。
 */
export class Pet implements EffectFlags {
    /**
     * roundNumber，保存当前实例的roundNumber状态。
     */
    roundNumber = 0;
    /** 对手等级的观察规则在战斗阶段开始判定，不改变原始能力等级。 */
    opponentLevelRule: "normal" | "ignore" | "invertPositive" = "normal";

    /**
     * effectiveLevel：执行对应的领域操作。
     * @param key key参数，参与本次操作
     * @param observer 观察能力等级的精灵
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    effectiveLevel(key: keyof AbilityLevel, observer?: Pet): number {
        /**
         * raw，供本模块相关规则使用。
         */
        const raw = this._abLvl[key];
        if (typeof raw !== "number") return 0;
        if (observer?.opponentLevelRule === "ignore") return 0;
        if (observer?.opponentLevelRule === "invertPositive" && raw > 0) return -raw;
        return raw;
    }

    /**
     * criticalStrike，保存当前实例的criticalStrike状态。
     */
    criticalStrike?: boolean;
    /**
     * addon，保存当前实例的addon状态。
     */
    addon?: number;
    /**
     * powerChange，保存当前实例的powerChange状态。
     */
    powerChange: (power: number) => number = unchangedPower;

    /**
     * 精灵序号（主键，唯一标识一只精灵）
     * @description 同时也是该精灵默认皮肤的 id；永随实例不变，不随皮肤切换而改变。
     */
    private _id: number;
    /**
     * 精灵支持的皮肤组
     * @description 至少包含一个默认皮肤（其 id === _id）；当前展示的皮肤由 _currentSkinIndex 指向。
     */
    private _skins: Skin[];
    /**
     * 当前皮肤在 _skins 中的索引
     */
    private _currentSkinIndex: number;
    /**
     * _type，保存当前实例的type状态。
     */
    private _type: Type;
    /**
     * _abVal，保存当前实例的abVal状态。
     */
    private _abVal: AbilityValue;

    /**
     * _abLvl，保存当前实例的abLvl状态。
     */
    private _abLvl: AbilityLevel;
    /**
     * _maxHp，保存当前实例的maxHp状态。
     */
    private _maxHp: number;
    /**
     * _curHp，保存当前实例的curHp状态。
     */
    private _curHp: number;
    /**
     * _resistance，保存当前实例的resistance状态。
     */
    private _resistance: Resistance;
    private _abnormalResistance: AbnormalResistance[] = [];
    /**
     * _soulmark，保存当前实例的soulmark状态。
     */
    private _soulmark: Soulmark;
    /**
     * _soulmarkInitialized，保存当前实例的soulmarkInitialized状态。
     */
    private _soulmarkInitialized = false;
    /**
     * _soulmarkEnabled，保存当前实例的soulmarkEnabled状态。
     */
    private _soulmarkEnabled = true;
    /**
     * _soulmarkCandidates，保存当前实例的soulmarkPermanents状态。
     */
    private _soulmarkCandidates: SoulmarkEffectCandidate[] = [];

    /**
     * 读取当前soulmarkEnabled。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get soulmarkEnabled() {
        return this._soulmarkEnabled;
    }

    /**
     * setSoulmarkEnabled：执行对应的领域操作。
     * @param enabled enabled参数，参与本次操作
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    setSoulmarkEnabled(enabled: boolean, context: Context) {
        this._soulmarkEnabled = enabled;
        reconcileSoulmarkEffects(context);
    }

    /**
     * getSoulmarkEffectCandidates：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    getSoulmarkEffectCandidates(): readonly SoulmarkEffectCandidate[] {
        return this._soulmarkCandidates;
    }


    /**
     * _skillGroup，保存当前实例的skillGroup状态。
     */
    private _skillGroup: Skill[];
    /**
     * _roundEffectGroup，保存当前实例的roundEffectGroup状态。
     */
    private _roundEffectGroup: RoundEffectGroup;
    /**
     * _consumeEffectGroup，保存当前实例的consumeEffectGroup状态。
     */
    private _consumeEffectGroup: ConsumeEffectGroup;
    /**
     * _permanentEffectGroup，保存当前实例的permanentEffectGroup状态。
     */
    private _permanentEffectGroup: EffectRegistry<Effect>;
    /**
     * _stateGroup，保存当前实例的stateGroup状态。
     */
    private _stateGroup: State[];
    /**
     * _damageShield，保存当前实例的damageShield状态。
     */
    private _damageShield: number;
    /**
     * _specialDamageShield，保存当前实例的specialDamageShield状态。
     */
    private _specialDamageShield: number;
    /**
     * _buffGroup，保存当前实例的buffGroup状态。
     */
    private _buffGroup: Buff[];
    /** 当前是否免疫异常/能力下降/固定伤害。 */
    private _immuneAbnormal: boolean;
    /**
     * _immuneAbilityDown，保存当前实例的immuneAbilityDown状态。
     */
    private _immuneAbilityDown: boolean;
    /**
     * _immuneFixedDamage，保存当前实例的immuneFixedDamage状态。
     */
    private _immuneFixedDamage: boolean;
    /** 需在下一回合生效的动态先制与吸取修正。 */
    private _nextRoundDrainFraction: number;
    /**
     * _blockedFixedDamage，保存当前实例的blockedFixedDamage状态。
     */
    private _blockedFixedDamage: number;
    /**
     * _skillPowerMultiplier，保存当前实例的skillPowerMultiplier状态。
     */
    private _skillPowerMultiplier: number;

    /**
     * _battleBoard，保存当前实例的battleBoard状态。
     */
    private _battleBoard: BattleBoard;
    /**
     * _battleStatus，保存当前实例的battleStatus状态。
     */
    private _battleStatus: BattleStatus;

    /**
     * 精灵
     * @param id 精灵序号（主键，同时为默认皮肤 id）
     * @param name 精灵名称（默认皮肤名称）
     * @param type 属性
     * @param abVal 精灵能力值
     */
    constructor(id: number, name: string, type: Type, abVal: AbilityValue) {
        this._id = id;
        this._skins = [new Skin(id, name)];
        this._currentSkinIndex = 0;
        this._type = type;
        this._abVal = normalizeAbilityValue(abVal);

        this._abLvl = new AbilityLevel();
        this._curHp = this._maxHp = this._abVal.体力;
        this._resistance = {
            criticalDamage: 0,
            fixedDamage: 0,
            percentDamage: 0,
        };
        this._soulmark = new Soulmark([]);

        this._skillGroup = [];
        this._roundEffectGroup = new RoundEffectGroup();
        this._consumeEffectGroup = new ConsumeEffectGroup();
        this._permanentEffectGroup = new EffectRegistry<Effect>();
        this._stateGroup = [];

        this._damageShield = 0;
        this._specialDamageShield = 0;
        this._buffGroup = [];
        this._immuneAbnormal = false;
        this._immuneAbilityDown = false;
        this._immuneFixedDamage = false;
        this._nextRoundDrainFraction = 0;
        this._blockedFixedDamage = 0;
        this._skillPowerMultiplier = 1;

        this._battleBoard = new BattleBoard();
        this._battleStatus = BattleStatus.STANDBY;
    }

    /**
     * 读取当前序号。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 序号() {
        return this._id;
    }

    /**
     * 更新当前序号。
     * @param value 待处理的值
     */
    set 序号(value: number) {
        this._id = value;
    }

    /**
     * 读取当前名字。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 名字() {
        return this.当前皮肤.name;
    }

    /**
     * 更新当前名字。
     * @param value 待处理的值
     */
    set 名字(value: string) {
        this.当前皮肤.name = value;
    }

    /**
     * 当前皮肤
     */
    get 当前皮肤() {
        return this._skins[this._currentSkinIndex];
    }

    /**
     * 当前皮肤在皮肤组中的索引
     */
    get 当前皮肤序号() {
        return this._currentSkinIndex;
    }

    /**
     * 精灵支持的皮肤组
     */
    get 皮肤组() {
        return this._skins;
    }

    /**
     * 切换当前皮肤
     * @param index 目标皮肤在皮肤组中的索引
     */
    切换皮肤(index: number) {
        if (index < 0 || index >= this._skins.length) {
            console.warn(`切换皮肤失败：索引 ${index} 越界（共 ${this._skins.length} 个皮肤）`);
            return;
        }
        this._currentSkinIndex = index;
    }

    /**
     * 追加一个皮肤
     * @param skin 待追加的皮肤
     */
    添加皮肤(skin: Skin) {
        this._skins.push(skin);
    }

    /**
     * 设置皮肤组（覆盖现有皮肤组）
     * @description 至少应包含一个 id === _id 的默认皮肤
     * @param skins 皮肤组
     */
    设置皮肤组(skins: Skin[]) {
        if (skins.length === 0) {
            console.warn("设置皮肤组失败：皮肤组不能为空");
            return;
        }
        this._skins = skins;
        this._currentSkinIndex = 0;
    }

    /**
     * 读取当前属性。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 属性() {
        return this._type;
    }

    /**
     * 更新当前属性。
     * @param value 待处理的值
     */
    set 属性(value: Type) {
        this._type = value;
    }

    /**
     * 读取当前能力值。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 能力值() {
        return this._abVal;
    }

    /**
     * 更新当前能力值。
     * @param value 待处理的值
     */
    set 能力值(value: AbilityValue) {
        this._abVal = normalizeAbilityValue(value);
    }

    /**
     * 读取当前能力等级。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 能力等级() {
        return this._abLvl;
    }

    /**
     * 更新当前能力等级。
     * @param value 待处理的值
     */
    set 能力等级(value: AbilityLevel) {
        this._abLvl = value;
    }

    /**
     * 读取当前最大体力。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 最大体力() {
        return this._maxHp;
    }

    /**
     * 更新当前最大体力。
     * @param value 待处理的值
     */
    set 最大体力(value: number) {
        this._maxHp = value;
    }

    /**
     * 读取当前当前体力。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 当前体力() {
        return this._curHp;
    }

    /**
     * 更新当前当前体力。
     * @param value 待处理的值
     */
    set 当前体力(value: number) {
        this._curHp = value;
    }

    /**
     * 读取当前抗性。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 抗性() {
        return { ...this._resistance };
    }

    /**
     * 更新当前抗性。
     * @param value 待处理的值
     */
    set 抗性(value: Resistance) {
        validateRates([value.criticalDamage, value.fixedDamage, value.percentDamage]);
        this._resistance = { ...value };
    }

    get 伤害抗性(): Readonly<DamageResistance> { return this.抗性; }
    set 伤害抗性(value: DamageResistance) { this.抗性 = value; }
    get 异常抗性(): ReadonlyArray<Readonly<AbnormalResistance>> { return this._abnormalResistance.map(item => ({ ...item })); }
    set 异常抗性(value: ReadonlyArray<Readonly<AbnormalResistance>>) {
        if (value.length > 3 || new Set(value.map(item => item.state)).size !== value.length || value.some(item => !abnormalNames.has(item.state))) throw new Error("异常抗性最多三个不同的异常状态");
        validateRates(value.map(item => item.rate));
        this._abnormalResistance = value.map(item => ({ ...item }));
    }

    /**
     * 读取当前魂印。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 魂印() {
        if (!this._soulmark) {
            throw new Error("魂印未设置");
        }
        return this._soulmark;
    }

    /**
     * 更新当前魂印。
     * @param value 待处理的值
     */
    set 魂印(value: Soulmark) {
        this._soulmark = cloneBattleState(value);
    }

    /**
     * 读取当前技能组。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 技能组() {
        return this._skillGroup;
    }

    /**
     * 更新当前技能组。
     * @param value 待处理的值
     */
    set 技能组(value: Skill[]) {
        this._skillGroup = cloneBattleState(value);
    }

    /**
     * 读取当前回合类效果。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 回合类效果() {
        return this._roundEffectGroup;
    }

    /**
     * 更新当前回合类效果。
     * @param value 待处理的值
     */
    set 回合类效果(value: RoundEffectGroup) {
        this._roundEffectGroup = value;
    }

    /**
     * 读取当前消耗类效果。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 消耗类效果() {
        return this._consumeEffectGroup;
    }

    /**
     * 更新当前消耗类效果。
     * @param value 待处理的值
     */
    set 消耗类效果(value: ConsumeEffectGroup) {
        this._consumeEffectGroup = value;
    }

    /**
     * 读取当前常驻类效果。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 常驻类效果() {
        return this._permanentEffectGroup;
    }

    /**
     * 更新当前常驻类效果。
     * @param value 待处理的值
     */
    set 常驻类效果(value: EffectRegistry<Effect>) {
        this._permanentEffectGroup = value;
    }

    /**
     * 读取当前异常状态。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 异常状态() {
        return this._stateGroup;
    }

    /**
     * 更新当前异常状态。
     * @param value 待处理的值
     */
    set 异常状态(value: State[]) {
        this._stateGroup = value;
    }

    /**
     * 读取当前护盾。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 护盾() {
        return this._damageShield;
    }

    /**
     * 更新当前护盾。
     * @param value 待处理的值
     */
    set 护盾(value: number) {
        this._damageShield = value;
    }

    /**
     * 读取当前护罩。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 护罩() {
        return this._specialDamageShield;
    }

    /**
     * 更新当前护罩。
     * @param value 待处理的值
     */
    set 护罩(value: number) {
        this._specialDamageShield = value;
    }

    /**
     * 读取当前战斗面板。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 战斗面板() {
        return this._battleBoard;
    }

    /**
     * 更新当前战斗面板。
     * @param value 待处理的值
     */
    set 战斗面板(value: BattleBoard) {
        this._battleBoard = value;
    }

    /** 当前精灵携带的 Buff 列表。 */
    get Buff组() {
        return this._buffGroup;
    }

    /** 英文别名，便于通用扩展使用。 */
    get buffs() {
        return this._buffGroup;
    }

    /**
     * 读取当前buffGroup。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get buffGroup() {
        return this._buffGroup;
    }

    /**
     * addBuff：执行对应的领域操作。
     * @param buff buff参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    addBuff(buff: Buff) {
        /**
         * existing，供本模块相关规则使用。
         */
        const existing = this._buffGroup.find((value) => value.constructor === buff.constructor);
        if (existing) return existing;
        this._buffGroup.push(buff);
        return buff;
    }

    /**
     * getBuff：执行对应的领域操作。
     * @param type type参数，参与本次操作
     */
    getBuff<T extends Buff>(type: new (...args: any[]) => T): T | undefined;
    /**
     * getBuff：执行对应的领域操作。
     * @param type type参数，参与本次操作
     */
    getBuff<T extends Buff>(type: string): T | undefined;
    /**
     * getBuff：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    getBuff<T extends Buff>(type: (new (...args: any[]) => T) | string): T | undefined {
        return this._buffGroup.find((value) =>
            typeof type === "string" ? value.name === type : value instanceof type,
        ) as T | undefined;
    }

    /**
     * removeBuff：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    removeBuff<T extends Buff>(type: new (...args: any[]) => T) {
        /**
         * index，供本模块相关规则使用。
         */
        const index = this._buffGroup.findIndex((value) => value instanceof type);
        if (index < 0) return false;
        this._buffGroup.splice(index, 1);
        return true;
    }

    /**
     * addBuffStacks：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @param stacks stacks参数，参与本次操作
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    addBuffStacks<T extends Buff>(
        type: new (...args: any[]) => T,
        stacks: number,
        context?: Context,
    ): number {
        /**
         * buff，供本模块相关规则使用。
         */
        const buff = this.getBuff(type) ?? this.addBuff(new type());
        /**
         * changed，供本模块相关规则使用。
         */
        const changed = buff.addStacks(stacks);
        if (changed !== 0) this.refreshBuffEffects(buff, context);
        return changed;
    }

    /**
     * 读取当前immuneAbnormal。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get immuneAbnormal() {
        return this._immuneAbnormal;
    }

    /**
     * 更新当前immuneAbnormal。
     * @param value 待处理的值
     */
    set immuneAbnormal(value: boolean) {
        this._immuneAbnormal = value;
    }

    /**
     * 读取当前immuneAbilityDown。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get immuneAbilityDown() {
        return this._immuneAbilityDown;
    }

    /**
     * 更新当前immuneAbilityDown。
     * @param value 待处理的值
     */
    set immuneAbilityDown(value: boolean) {
        this._immuneAbilityDown = value;
    }

    /**
     * 读取当前immuneFixedDamage。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get immuneFixedDamage() {
        return this._immuneFixedDamage;
    }

    /**
     * 更新当前immuneFixedDamage。
     * @param value 待处理的值
     */
    set immuneFixedDamage(value: boolean) {
        this._immuneFixedDamage = value;
    }

    /**
     * 读取当前nextRoundDrainFraction。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get nextRoundDrainFraction() {
        return this._nextRoundDrainFraction;
    }

    /**
     * 读取当前skillPowerMultiplier。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get skillPowerMultiplier() {
        return this._skillPowerMultiplier;
    }

    /**
     * 更新当前skillPowerMultiplier。
     * @param value 待处理的值
     */
    set skillPowerMultiplier(value: number) {
        this._skillPowerMultiplier = value;
    }

    /**
     * addNextRoundReward：执行对应的领域操作。
     * @param drainFraction drainFraction参数，参与本次操作
     */
    addNextRoundReward(drainFraction: number) {
        this._nextRoundDrainFraction += Math.max(0, drainFraction);
    }

    /**
     * consumeNextRoundReward：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    consumeNextRoundReward() {
        /**
         * reward，供本模块相关规则使用。
         */
        const reward = {
            drainFraction: this._nextRoundDrainFraction,
        };
        this._nextRoundDrainFraction = 0;
        return reward;
    }

    /** 奖励在回合开始激活，整个本轮排序与结算期间保留。 */
    activateNextRoundReward(context: Context) {
        /**
         * reward，供本模块相关规则使用。
         */
        const reward = this.consumeNextRoundReward();
        if (reward.drainFraction > 0) {
            /**
             * amount，供本模块相关规则使用。
             */
            const amount = Math.round(context.objPet.最大体力 * reward.drainFraction);
            context.objPet.战斗面板.percentDamage = this.causePercentDamage(amount, context.objPet);
            this.战斗面板.recoverHP = this.recover(amount);
        }
    }

    /** 供效果在直接修改专有 Buff 数据后显式刷新魂印。 */
    refreshBuffEffects(buff: Buff, context?: Context) {
        if (context) {
            /**
             * sameSide，供本模块相关规则使用。
             */
            const sameSide = context.sbjSeer.petPackage.includes(this);
            /**
             * owner，供本模块相关规则使用。
             */
            const owner = sameSide ? context.sbjSeer : context.objSeer;
            /**
             * opponent，供本模块相关规则使用。
             */
            const opponent = sameSide ? context.objSeer : context.sbjSeer;
            dispatchBattleTiming(Timing.buffChanged, {
                ...context,
                buff,
                sbjPet: this,
                sbjSeer: owner,
                objSeer: opponent,
                objPet: opponent.getBattlePet() ?? context.objPet,
                side: sameSide ? context.side : opposite(context.side ?? "left"),
            });
        }
        // 未进入战斗的纯数据设置由 battleBegin 同步。
    }

    /** 所有已选精灵在 battleBegin 之前准备；快照恢复、返场不重建。 */
    initializeSoulmark(source: string) {
        if (this._soulmarkInitialized) return;
        /**
         * effects，供本模块相关规则使用。
         */
        const effects = cloneBattleState(this._soulmark.effectGroup);
        /**
         * mark，供本模块相关规则使用。
         */
        const mark = (effect: Effect, key: string) => {
            effect.soulmarkSource = key;
            effect.subEffects.forEach((child, index) => mark(child, `${key}/${index}`));
        };
        effects.forEach((effect, index) => {
            if (
                effect.timing === undefined ||
                effect.timing === Timing.triggered ||
                effect.timing === Timing.untriggered
            )
                throw new Error("魂印顶层效果必须指定正常触发时机");
            mark(effect, `${source}/${index}`);
        });
        this._soulmarkCandidates = effects.map((definition) => ({
            definition,
            targetId: this.序号,
            // 有限声明始终由授予者监听，其生成实例再按 bindEnemy 绑定目标。
            bindEnemy: !(definition instanceof RoundEffect || definition instanceof ConsumeEffect)
                && definition.bindEnemy,
        }));
        this._soulmarkInitialized = true;
    }

    /** 捕获当前持有的魂印声明及其有限实例，按各自时机执行。 */
    captureSoulmarkTiming(timing: Timing, context: Context) {
        /**
         * eventPet，供本模块相关规则使用。
         */
        const eventPet = context.eventPet ?? context.sbjPet;
        /**
         * eventSeer，供本模块相关规则使用。
         */
        const eventSeer = context.eventSeer ?? context.sbjSeer;
        /**
         * ownEvent，供本模块相关规则使用。
         */
        const ownEvent = eventPet === this;
        /**
         * matches，供本模块相关规则使用。
         */
        const matches = (effect: Effect, declared: boolean) => {
            if (this.战斗状态 === BattleStatus.DEAD && !(ownEvent && timing === Timing.petDead))
                return false;
            if (
                this.战斗状态 === BattleStatus.STANDBY &&
                !effect.allowStandby &&
                !(ownEvent && timing === Timing.battleBegin)
            )
                return false;
            if (effect.listenScope === "team") return context.sbjSeer === eventSeer;
            if (effect.listenScope === "battle") return true;
            /**
             * registration，供本模块相关规则使用。
             */
            const registration =
                declared &&
                (effect instanceof RoundEffect ||
                    effect instanceof ConsumeEffect);
            return registration || effect.effect.perspective === "self"
                ? ownEvent
                : context.eventTarget === this && !ownEvent;
        };
        /**
         * declared，供本模块相关规则使用。
         */
        const declared = this._permanentEffectGroup
            .get(timing)
            .filter((effect) => effect.soulmarkSource && matches(effect, true));
        /**
         * active，供本模块相关规则使用。
         */
        const active = [
            ...this._roundEffectGroup.get(timing),
            ...this._consumeEffectGroup.get(timing),
        ].filter((effect) => effect.soulmarkSource && matches(effect, false));
        /**
         * executed，供本模块相关规则使用。
         */
        const executed = new Set<Effect>();
        return (filter: (effect: Effect, definition: boolean) => boolean = () => true) => {
            // 命中阶段的声明先注册，回合效果在执行所属通道时再捕获。
            /**
             * currentActive，供本模块相关规则使用。
             */
            const currentActive = timing === Timing.onSkillHit
                ? [...active.filter((effect) => !(effect instanceof RoundEffect)),
                    ...this._roundEffectGroup.get(timing).filter((effect) =>
                        effect.soulmarkSource && matches(effect, false))]
                : active;
            for (const effect of [...declared, ...currentActive]) {
                if (executed.has(effect) || !matches(effect, declared.includes(effect))) continue;
                /**
                 * definition，供本模块相关规则使用。
                 */
                const definition = declared.includes(effect);
                if (definition && !this._permanentEffectGroup.get(timing).includes(effect)) continue;
                const finiteDeclaration = definition &&
                    (effect instanceof RoundEffect || effect instanceof ConsumeEffect);
                if (effect.effect.queryOnly && !finiteDeclaration) continue;
                if (!filter(effect, definition)) continue;
                if (
                    !definition &&
                    ![
                        ...this._permanentEffectGroup.get(timing),
                        ...this._roundEffectGroup.get(timing),
                        ...this._consumeEffectGroup.get(timing),
                    ].includes(effect)
                )
                    continue;
                executed.add(effect);
                /**
                 * succeeded，供本模块相关规则使用。
                 */
                const succeeded = definition
                    ? effect.onEffect(context)
                    : effect instanceof RoundEffect
                      ? effect.onRoundEffect(context)
                      : effect instanceof ConsumeEffect
                        ? effect.onConsumeEffect(context)
                        : effect.onEffect(context);
                context.events?.push({
                    timing,
                    side: context.side,
                    petId: this.序号,
                    eventPetId: eventPet.序号,
                    effect: effect.effect.constructor.name,
                    source: effect.soulmarkSource,
                    succeeded,
                });
            }
            this._consumeEffectGroup.retain((effect) => effect.remainTriggerNum > 0);
        };
    }

    /** 移除指定来源生成的常驻效果，不影响技能或其他来源的效果。 */
    unregisterPersistentEffectsBySource(source: string) {
        this._permanentEffectGroup.retain((effect) => effect.source !== source);
    }

    /**
     * consumeBlockedFixedDamage：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    consumeBlockedFixedDamage() {
        /**
         * damage，供本模块相关规则使用。
         */
        const damage = this._blockedFixedDamage;
        this._blockedFixedDamage = 0;
        return damage;
    }

    /**
     * changeAbilityLevel：执行对应的领域操作。
     * @param delta delta参数，参与本次操作
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    changeAbilityLevel(delta: DeltaAbilityLevel, context?: Context) {
        /**
         * decreasing，供本模块相关规则使用。
         */
        const decreasing = Object.values(delta).some((value) => (value ?? 0) < 0);
        if (
            !this._immuneAbilityDown &&
            !(decreasing && this.queryProtection(免疫能力下降, context))
        ) {
            this._abLvl.change(delta);
            return;
        }
        /**
         * allowed，供本模块相关规则使用。
         */
        const allowed = Object.fromEntries(
            Object.entries(delta).filter(([, value]) => (value ?? 0) >= 0),
        ) as DeltaAbilityLevel;
        this._abLvl.change(allowed);
    }

    /**
     * lowerAllAbilityLevels：执行对应的领域操作。
     * @param levels levels参数，参与本次操作
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    lowerAllAbilityLevels(levels: number, context?: Context) {
        if (this._immuneAbilityDown || this.queryProtection(免疫能力下降, context)) return false;
        this._abLvl.allLevelDown(levels);
        return true;
    }

    /**
     * getSkillPreemptiveLevel：执行对应的领域操作。
     * @param skill 本次使用的技能
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    getSkillPreemptiveLevel(skill: Skill, context?: Context) {
        if (context) reconcileSoulmarkEffects(context);
        /**
         * query，供本模块相关规则使用。
         */
        const query = context
            ? { ...this.ownerContext(context), skill, priorityBonus: 0 }
            : undefined;
        if (query)
            for (const effect of this.queryableEffects()) {
                if (effect.effect instanceof 先制) this.invokeQueryEffect(effect, query);
            }
        return (
            (query?.priorityBonus ?? 0) +
            skill.preemptiveLevel
        );
    }

    /**
     * 重置战斗面板与 Effect 临时标志
     * 用于回合内每步行动前避免跨步污染
     */
    resetBattleBoard() {
        this._battleBoard.reset();
        this.criticalStrike = undefined;
        this.addon = undefined;
        this.powerChange = unchangedPower;
    }

    /**
     * 读取当前战斗状态。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get 战斗状态() {
        return this._battleStatus;
    }

    /**
     * 更新当前战斗状态。
     * @param value 待处理的值
     */
    set 战斗状态(value: BattleStatus) {
        this._battleStatus = value;
    }

    /**
     * 获取精灵头像图片URL
     * @returns 返回精灵头像图片的完整URL地址
     */
    getHeadImgUrl() {
        return this.当前皮肤.getHeadImgUrl();
    }

    /**
     * 获取精灵战斗动画资源 URL
     * 使用经 ffdec 补丁后的本地 swf（帧标签提升到根时间轴，
     * 使 Ruffle 的 gotoAndStop 可直达 stand/attack/sa/cp 动画）。
     * @returns swf 格式的动画资源 URL
     */
    getBattleAnimUrl() {
        return this.当前皮肤.getBattleAnimUrl();
    }

    /**
     * 造成伤害
     * @param damage 伤害
     * @param obj 目标
     * @param addon 直接增伤
     * @param scale 比例增伤
     * @param criticalStrike 是否暴击
     * @returns 目标实际受到的伤害
     */
    causeDamage(
        damage: number,
        obj: Pet,
        addon: number = 0,
        scale: number = 1,
        criticalStrike?: boolean,
        minDamage: number = 0,
    ) {
        criticalStrike = criticalStrike || false;
        damage = Math.max(damage + addon, 0);
        damage = Math.round(damage * scale);
        return obj.sufferDamage(damage, criticalStrike, minDamage);
    }

    /**
     * 造成固定伤害
     * @param damage 固定伤害
     * @param obj 目标
     * @param addon 直接增伤
     * @param scale 比例增伤
     * @returns 目标实际受到的固定伤害
     */
    causeFixedDamage(
        damage: number,
        obj: Pet,
        addon: number = 0,
        scale: number = 1,
        context?: Context,
    ) {
        return obj.sufferFixedDamage((damage + addon) * scale, context);
    }

    /**
     * 造成百分比伤害
     * @param damage 百分比伤害
     * @param obj 目标
     * @param addon 直接增伤
     * @param scale 比例增伤
     * @returns 目标实际受到的百分比伤害
     */
    causePercentDamage(damage: number, obj: Pet, addon: number = 0, scale: number = 1) {
        return obj.sufferPercentDamage((damage + addon) * scale);
    }

    /**
     * 受到伤害
     * @param damage 攻击方造成的伤害
     * @returns 自身实际受到的伤害
     */
    sufferDamage(damage: number, criticalStrike: boolean, minDamage: number = 0) {
        if (criticalStrike) {
            damage *= 1 - this._resistance.criticalDamage;
        }
        damage = Math.max(Math.round(damage), Math.ceil(minDamage));
        this._curHp = Math.max(0, this._curHp - damage);
        return damage;
    }

    /**
     * 受到固定伤害
     * @param damage 攻击方造成的固定伤害
     * @returns 自身实际受到的固定伤害
     */
    sufferFixedDamage(damage: number, context?: Context): number {
        if (this._immuneFixedDamage || this.queryProtection(免疫固定伤害, context)) {
            this._blockedFixedDamage += Math.max(0, Math.round(damage));
            return 0;
        }
        damage = Math.round(damage * (1 - this._resistance.fixedDamage));
        this._curHp = Math.max(0, this._curHp - damage);
        if (this._curHp < 0) {
            this._curHp = 0;
        }
        return damage;
    }

    /**
     * 受到百分比伤害
     * @param damage 攻击方造成的百分比伤害
     * @returns 自身实际受到的百分比伤害
     */
    /** 直接流失体力，不经过伤害抗性、免疫或护盾。 */
    loseHp(amount: number): number {
        const loss = Math.min(this._curHp, Math.max(0, Math.round(amount)));
        this._curHp -= loss;
        return loss;
    }

    sufferPercentDamage(damage: number): number {
        damage = Math.round(damage * (1 - this._resistance.percentDamage));
        this._curHp = Math.max(0, this._curHp - damage);
        if (this._curHp < 0) {
            this._curHp = 0;
        }
        return damage;
    }

    /**
     * 恢复体力
     * @param hp 待恢复的体力值
     * @returns 实际恢复的体力值
     */
    recover(hp: number): number {
        /**
         * curHp，供本模块相关规则使用。
         */
        let curHp = this._curHp;
        this._curHp += hp;
        if (this._curHp > this._maxHp) {
            this._curHp = this._maxHp;
        }
        return this._curHp - curHp;
    }

    /**
     * registerRoundEffect：执行对应的领域操作。
     * @param effect 待处理的效果实例
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    registerRoundEffect(effect: RoundEffect) {
        if (effect.effectTiming === undefined || effect.totalRoundNum <= 0) return false;
        /**
         * instance，供本模块相关规则使用。
         */
        const instance = cloneBattleState(effect);
        instance.reset();
        instance.activeFromRound = this.roundNumber + instance.activationDelay;
        return this.registerEffect(this._roundEffectGroup, instance);
    }

    /**
     * registerConsumeEffect：执行对应的领域操作。
     * @param effect 待处理的效果实例
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    registerConsumeEffect(effect: ConsumeEffect) {
        if (effect.effectTiming === undefined || effect.totalTriggerNum <= 0) return false;
        /**
         * instance，供本模块相关规则使用。
         */
        const instance = cloneBattleState(effect);
        instance.reset();
        return this.registerEffect(this._consumeEffectGroup, instance);
    }

    /**
     * registerPersistentEffect：执行对应的领域操作。
     * @param effect 待处理的效果实例
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    registerPersistentEffect(effect: Effect) {
        return this.registerEffect(this._permanentEffectGroup, cloneBattleState(effect), effect.timing);
    }

    /**
     * registerEffect：执行对应的领域操作。
     * @param registry registry参数，参与本次操作
     * @param effect 待处理的效果实例
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private registerEffect<T extends Effect>(
        registry: import("./skill").EffectRegistry<T>,
        effect: T,
        timing = effect instanceof RoundEffect || effect instanceof ConsumeEffect ? effect.effectTiming : effect.timing,
    ): boolean {
        if (timing === undefined) return false;
        /**
         * effects，供本模块相关规则使用。
         */
        const effects = registry.get(timing);
        /**
         * retained，供本模块相关规则使用。
         */
        const retained = effects.filter((existing) => {
            if (effect.soulmarkSource || existing.soulmarkSource)
                return existing.soulmarkSource !== effect.soulmarkSource;
            return (
                existing.effect.constructor !== effect.effect.constructor ||
                (effect.effect instanceof 先制 && existing.effect instanceof 先制 && existing.prioritySource !== effect.prioritySource) ||
                existing.source !== effect.source
            );
        });
        effects.splice(0, effects.length, ...retained, effect);
        return true;
    }

    /**
     * unregisterPersistentEffect：执行对应的领域操作。
     * @param effect 待处理的效果实例
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    unregisterPersistentEffect(effect: Effect) {
        /**
         * found，供本模块相关规则使用。
         */
        const found = this._permanentEffectGroup.all().includes(effect);
        this._permanentEffectGroup.retain((candidate) => candidate !== effect);
        return found;
    }

    /**
     * hasRoundEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    hasRoundEffect() {
        return this._roundEffectGroup.all().length > 0;
    }
    /**
     * hasConsumeEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    hasConsumeEffect() {
        return this._consumeEffectGroup.all().length > 0;
    }
    /**
     * hasPersistentEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    hasPersistentEffect() {
        return this._permanentEffectGroup.all().length > 0;
    }

    /**
     * destroyRoundEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    destroyRoundEffect() {
        /**
         * existed，供本模块相关规则使用。
         */
        const existed = this.hasRoundEffect();
        this._roundEffectGroup = new RoundEffectGroup();
        return existed;
    }

    /**
     * destroyConsumeEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    destroyConsumeEffect() {
        /**
         * existed，供本模块相关规则使用。
         */
        const existed = this.hasConsumeEffect();
        this._consumeEffectGroup = new ConsumeEffectGroup();
        return existed;
    }

    /**
     * destroyPersistentEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    destroyPersistentEffect() {
        /**
         * existed，供本模块相关规则使用。
         */
        const existed = this.hasPersistentEffect();
        this._permanentEffectGroup = new EffectRegistry<Effect>();
        return existed;
    }

    /** 触发与计时分离，控场、道具、切换和空过也会到达这里。 */
    tickDurations() {
        if (this.战斗状态 === BattleStatus.DEAD) return;
        for (const effect of this._roundEffectGroup.all()) {
            if (effect.lifetime === "actions" && this.roundNumber >= effect.activeFromRound)
                effect.pass();
        }
        this._roundEffectGroup.retain((effect) => effect.remainRoundNum > 0);
        this.settleStates();
        for (const buff of this._buffGroup) buff.tickDurations();

    }

    /**
     * expireRoundProtection：执行对应的领域操作。
     */
    expireRoundProtection() {
        this._roundEffectGroup.retain((effect) => effect.lifetime !== "round");
    }

    /** 通常在阶段开始前捕获；命中阶段允许刚注册的回合效果参与本次结算。 */
    captureTiming(
        timing: Timing,
        context: Context,
        definitions: Effect[] = [],
        perspective: "self" | "opponent" = "self",
    ) {
        /**
         * permanent，供本模块相关规则使用。
         */
        const permanent = this._permanentEffectGroup
            .get(timing)
            .filter((effect) => !effect.soulmarkSource);
        /**
         * rounds，供本模块相关规则使用。
         */
        const rounds = this._roundEffectGroup
            .get(timing)
            .filter((effect) => !effect.soulmarkSource);
        /**
         * consumes，供本模块相关规则使用。
         */
        const consumes = this._consumeEffectGroup
            .get(timing)
            .filter((effect) => !effect.soulmarkSource);
        /**
         * declared，供本模块相关规则使用。
         */
        const declared = [...definitions];
        /**
         * executed，供本模块相关规则使用。
         */
        const executed = new Set<Effect>();
        return (filter: (effect: Effect, definition: boolean) => boolean = () => true) => {
            /**
             * activeMatches，供本模块相关规则使用。
             */
            const activeMatches = (effect: Effect) =>
                !executed.has(effect) &&
                effect.effect.perspective === perspective &&
                !effect.effect.queryOnly &&
                filter(effect, false);
            /**
             * record，供本模块相关规则使用。
             */
            const record = (effect: Effect, succeeded: boolean) => {
                executed.add(effect);
                context.events?.push({
                    timing,
                    side: context.side,
                    effect: effect.effect.constructor.name,
                    succeeded,
                });
            };
            for (const effect of declared) {
                // 包装器注册监听自身行动；内部业务效果激活后才按其自身/对方视角响应。
                /**
                 * registration，供本模块相关规则使用。
                 */
                const registration =
                    effect instanceof RoundEffect ||
                    effect instanceof ConsumeEffect;
                if (
                    (registration
                        ? perspective !== "self"
                        : effect.effect.perspective !== perspective) ||
                    executed.has(effect) ||
                    !filter(effect, true) ||
                    (effect.hitEffect && context.action?.hitEffectsInvalid)
                )
                    continue;
                record(effect, effect.onEffect(context));
            }
            for (const effect of permanent) {
                if (
                    activeMatches(effect) &&
                    this._permanentEffectGroup.get(timing).includes(effect)
                )
                    record(effect, effect.onEffect(context));
            }
            /**
             * currentRounds，供本模块相关规则使用。
             */
            const currentRounds = timing === Timing.onSkillHit
                ? this._roundEffectGroup.get(timing).filter((effect) => !effect.soulmarkSource)
                : rounds;
            for (const effect of currentRounds) {
                if (activeMatches(effect) && this._roundEffectGroup.get(timing).includes(effect))
                    record(effect, effect.onRoundEffect(context));
            }
            for (const effect of consumes) {
                if (activeMatches(effect) && this._consumeEffectGroup.get(timing).includes(effect))
                    record(effect, effect.onConsumeEffect(context));
            }
            this._consumeEffectGroup.retain((effect) => effect.remainTriggerNum > 0);
        };
    }

    /**
     * runTiming：执行对应的领域操作。
     * @param timing 需要处理的战斗时机
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @param definitions 当前时机的技能效果声明；默认值为 `[]`
     * @param perspective perspective参数，参与本次操作；默认值为 `"self"`
     */
    runTiming(
        timing: Timing,
        context: Context,
        definitions: Effect[] = [],
        perspective: "self" | "opponent" = "self",
    ) {
        if (perspective === "opponent") {
            this.captureTiming(timing, context, definitions, perspective)();
        } else {
            /**
             * hooks，供本模块相关规则使用。
             */
            const hooks = captureBattleTiming(timing, { ...context, timing }, definitions);
            hooks.attacker();
            hooks.observers(undefined, true);
        }
    }

    /** 非技能公共时机只派发明确声明在该公共时机的效果。 */
    runGlobalTiming(timing: Timing, context: Context) {
        this.runTiming(
            timing,
            context,
            this._skillGroup.flatMap((skill) => skill.getEffectsAt(timing)),
        );
    }

    /**
     * 根据能力强化/弱化等级计算实际能力值（不包括命中）
     * @param abVal 能力值
     * @param abLvl 能力强化/弱化等级
     * @returns 实际能力值
     */
    getBattleAbilityValue(abVal: number, abLvl: number) {
        if (abLvl >= 0) {
            return abVal * (1 + abLvl / 2);
        } else {
            return abVal / (1 - abLvl / 2);
        }
    }

    /**
     * 进入战斗
     */
    enterBattle() {
        this._battleStatus = BattleStatus.FIGHTING;
        console.info(`【${this.名字}】进入战斗`);
    }

    /** 恢复一只已阵亡精灵，供复活类魂印使用。 */
    revive() {
        this.leaveBattle();
        this._curHp = this._maxHp;
        this._consumeEffectGroup = new ConsumeEffectGroup();
    }

    /** 击败对手后的通用回调。 */
    onDefeat(_objPet: Pet, _sbjSeer: Seer, _objSeer: Seer) {}

    /** 自身被击败后的通用回调。 */
    onDefeated(_objPet: Pet, _sbjSeer: Seer, _objSeer: Seer) {}

    /**
     * 离开战斗并清理仅在场时有效的状态
     * @param nextStatus 离场后的战斗状态；主动切换为待命，阵亡时为阵亡
     */
    leaveBattle(nextStatus: BattleStatus.STANDBY | BattleStatus.DEAD = BattleStatus.STANDBY) {
        this._roundEffectGroup = new RoundEffectGroup();
        this._immuneAbnormal = false;
        this._immuneAbilityDown = false;
        this._immuneFixedDamage = false;
        this._skillPowerMultiplier = 1;
        this.opponentLevelRule = "normal";
        this._nextRoundDrainFraction = 0;
        this._blockedFixedDamage = 0;
        this._stateGroup = [];
        this._abLvl.reset();
        this._battleStatus = nextStatus;
        if (nextStatus === BattleStatus.DEAD) this._curHp = 0;
        console.info(`【${this.名字}】离开战斗`);
    }

    /**
     *
     * @returns 是否存活
     */
    isAlive() {
        return this._curHp > 0 && this._battleStatus !== BattleStatus.DEAD;
    }

    /**
     * 精灵战败
     * @param kwargs 战斗参数
     */
    defeat() {
        if (this._battleBoard.damage) {
            if (this._battleBoard.damage > this._curHp) {
                this._battleBoard.damage = this._curHp;
            } else {
                this._battleBoard.damage += this._curHp;
            }
        } else {
            this._battleBoard.damage = this._curHp;
        }
        this._curHp = 0;
        return this._battleBoard.damage;
    }

    /** 查询时始终以实际受保护者为持有者，不保留跨快照上下文引用。 */
    private ownerContext(context: Context): Context {
        /**
         * sameSide，供本模块相关规则使用。
         */
        const sameSide = context.sbjSeer.petPackage.includes(this);
        /**
         * owner，供本模块相关规则使用。
         */
        const owner = sameSide ? context.sbjSeer : context.objSeer;
        /**
         * opponent，供本模块相关规则使用。
         */
        const opponent = sameSide ? context.objSeer : context.sbjSeer;
        return {
            ...context,
            sbjPet: this,
            sbjSeer: owner,
            objSeer: opponent,
            objPet: opponent.getBattlePet() ?? context.objPet,
            side: sameSide ? context.side : opposite(context.side ?? "left"),
            eventPet: context.eventPet ?? context.sbjPet,
            eventTarget: context.eventTarget ?? context.objPet,
            eventSeer: context.eventSeer ?? context.sbjSeer,
            eventSide: context.eventSide ?? context.side,
        };
    }

    /**
     * queryableEffects：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private queryableEffects() {
        if (this.战斗状态 === BattleStatus.DEAD) return [];
        return [
            ...this._permanentEffectGroup.all().filter((effect) => !(effect instanceof RoundEffect || effect instanceof ConsumeEffect)),
            ...this._roundEffectGroup.all(),
            ...this._consumeEffectGroup.all(),
        ].filter(
            (effect) =>
                !effect.soulmarkSource ||
                this.战斗状态 !== BattleStatus.STANDBY ||
                effect.allowStandby,
        );
    }

    /**
     * invokeQueryEffect：执行对应的领域操作。
     * @param effect 待处理的效果实例
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private invokeQueryEffect(
        effect: Effect,
        context: Context,
    ) {
        /**
         * succeeded，供本模块相关规则使用。
         */
        const succeeded =
            effect instanceof RoundEffect
                ? effect.onRoundEffect(context)
                : effect instanceof ConsumeEffect
                  ? effect.onConsumeEffect(context)
                  : effect.onEffect(context);
        this._consumeEffectGroup.retain((candidate) => candidate.remainTriggerNum > 0);
        context.events?.push({
            timing: context.timing ?? (effect instanceof RoundEffect || effect instanceof ConsumeEffect ? effect.effectTiming! : effect.timing!),
            side: context.side,
            petId: this.序号,
            eventPetId: context.eventPet?.序号 ?? context.sbjPet.序号,
            effect: effect.effect.constructor.name,
            source: effect.soulmarkSource,
            succeeded,
        });
        return succeeded;
    }

    /**
     * queryProtection：执行对应的领域操作。
     * @param type type参数，参与本次操作
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    queryProtection(type: new () => BaseEffect, context?: Context) {
        if (!context) return false;
        reconcileSoulmarkEffects(context);
        /**
         * query，供本模块相关规则使用。
         */
        const query = this.ownerContext(context);
        return this.queryableEffects().some(
            (effect) => effect.effect instanceof type && this.invokeQueryEffect(effect, query),
        );
    }

    /**
     * 陷入异常状态
     * @param state 异常状态
     * @param roundNum 已确定的持续回合数
     */
    fallIntoState(state: State, roundNum: number): boolean {
        const independent = cloneBattleState(state);
        independent.totalRoundNum = independent.remainRoundNum = roundNum;
        this._stateGroup = this._stateGroup.filter(
            (value) => value.constructor !== state.constructor,
        );
        this._stateGroup.push(independent);
        return true;
    }

    /**
     * 出手结束时结算异常状态：持续回合数 -1，到期（<=0）的状态自动解除
     * @returns 本回合解除的状态名列表
     */
    settleStates(): string[] {
        /**
         * relieved，供本模块相关规则使用。
         */
        const relieved: string[] = [];
        this._stateGroup = this._stateGroup.filter((state) => {
            if (state instanceof 烧伤 || state instanceof 冻伤 || state instanceof 中毒) {
                this.战斗面板.hpLoss = (this.战斗面板.hpLoss ?? 0) +
                    this.loseHp(Math.round(this.最大体力 / 6));
            }
            state.remainRoundNum -= 1;
            if (state.remainRoundNum <= 0) {
                relieved.push(state.name);
                return false;
            }
            return true;
        });
        return relieved;
    }
}
